// The impact analysis-recommendation transport (contract 03 §6): two GETs, no state, no i18n.
//
// A class of its own rather than two more methods on `CausalApi`, matching how module 23 keeps its
// recommendation reads separate from the feature's main API class. Shares `CausalApi`'s base path
// (`workflow/causal-single`) and its `CausalScope`.
import type { AxiosInstance, AxiosRequestConfig } from 'axios'

import { decodeImpactRecommendationExecution } from '../models/recommendation.codec'
import type { ImpactRecommendationExecution } from '../models/recommendation.types'

import { causalHttp, type CausalScope } from './CausalApi'

import { ApiError, decodeDto, unwrap } from '@/shared/http'

function requestConfig(signal?: AbortSignal): AxiosRequestConfig {
  return {
    suppressGlobalErrorHandler: true,
    meta: { suppressGlobalErrorHandler: true },
    ...(signal ? { signal } : {}),
  }
}

function base(scope: CausalScope): string {
  return `/accounts/${scope.accountId}/workspaces/${scope.workspaceId}/workflow/causal-single/analysis-recommendations`
}

async function featureRequest<T>(request: Promise<T>): Promise<T> {
  try {
    return await request
  } catch (cause) {
    throw ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
  }
}

export class ImpactRecommendationApi {
  constructor(private readonly http: AxiosInstance = causalHttp) {}

  /** The newest scan for the workspace — what the landing strip renders. */
  async getLatest(
    scope: CausalScope,
    signal?: AbortSignal,
  ): Promise<ImpactRecommendationExecution> {
    const response = await featureRequest(
      this.http.get(`${base(scope)}/latest/`, requestConfig(signal)),
    )
    return decodeDto(unwrap<unknown>(response), decodeImpactRecommendationExecution)
  }

  /** One named scan — the `?reco_execution=` deep link, which may point at an older run. */
  async getExecution(
    scope: CausalScope,
    executionId: string,
    signal?: AbortSignal,
  ): Promise<ImpactRecommendationExecution> {
    const response = await featureRequest(
      this.http.get(`${base(scope)}/${encodeURIComponent(executionId)}/`, requestConfig(signal)),
    )
    return decodeDto(unwrap<unknown>(response), decodeImpactRecommendationExecution)
  }
}

export const impactRecommendationApi = new ImpactRecommendationApi()
